/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * Staffing API
 * OpenAPI spec version: 1.0.0
 */
import type {
  AddMicrosoftCalendarPayload,
  AddMicrosoftCalendarResponse,
  AutoJoinMeetingCopilotResponse,
  BulkCalendarUpdateResult,
  BulkUpdateAvailabilityPayload,
  CalendarDisconnectResponse,
  CalendarDisconnectUserCalendarParams,
  CalendarIntegrationResponse,
  CalendarOAuthCallbackPayload,
  CalendarOAuthConsentResponse,
  CalendarOAuthInitiatePayload,
  CalendarRunCalendarHealthOperationsParams,
  CalendarSubscriptionRegisteredResponse,
  CreateCalendarAvailabilityPayload,
  CreateVirtualCalendarPayload,
  DomainUsersResponse,
  DomainWideDelegationValidationResponse,
  DomainsResponse,
  GetCalendarAvailabilityResponse,
  IntegrationHealthOperationsResponse,
  ScheduledInterviewActionResponse,
  SetAutoJoinMeetingCopilotRequest,
  SetCalendarIntegrationTypePayload,
  SetCalendarIntegrationTypeResponse,
  TestBookMeetingPayload,
  TestBookMeetingResponse,
  UpdateVirtualCalendarPayload,
  UserCalendarIntegrationResponse,
  VirtualCalendarResponse,
  ZoomCredentialsPayload,
  ZoomCredentialsResponse,
  ZoomSaveResponse,
  ZoomValidateResponse,
} from '../types';

import { axiosInstance } from '../../axiosConfig';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getCalendar = () => {
  /**
 * Add Google Calendar integration for an organization using service account credentials.

This endpoint sets up Google Calendar integration for the specified organization
using domain-wide delegation with service account credentials.

Returns:
    Dictionary containing calendar_client details and connected domains

Raises:
    HTTPException: 400 for calendar handler errors, 404 for user not found, 500 for server errors
 * @summary Add Org Google Calendar
 */
  const calendarAddOrgGoogleCalendar = (
    options?: SecondParameter<typeof axiosInstance<DomainsResponse>>
  ) => {
    return axiosInstance<DomainsResponse>(
      { url: `/integrations/google-calendar`, method: 'POST' },
      options
    );
  };
  /**
 * Add Microsoft Calendar integration for an organization.

This endpoint sets up Microsoft Graph API integration for the specified organization
using the provided tenant ID.

Args:
    payload: Request payload containing tenant_id

Returns:
    Dictionary containing calendar_client details

Raises:
    HTTPException: 400 for calendar handler errors, 500 for server errors
 * @summary Add Org Microsoft Calendar
 */
  const calendarAddOrgMicrosoftCalendar = (
    addMicrosoftCalendarPayload: AddMicrosoftCalendarPayload,
    options?: SecondParameter<
      typeof axiosInstance<AddMicrosoftCalendarResponse>
    >
  ) => {
    return axiosInstance<AddMicrosoftCalendarResponse>(
      {
        url: `/integrations/outlook-calendar`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: addMicrosoftCalendarPayload,
      },
      options
    );
  };
  /**
   * Run live checks against the connected calendar provider APIs.
   * @summary Run Calendar Health Operations
   */
  const calendarRunCalendarHealthOperations = (
    params?: CalendarRunCalendarHealthOperationsParams,
    options?: SecondParameter<
      typeof axiosInstance<IntegrationHealthOperationsResponse>
    >
  ) => {
    return axiosInstance<IntegrationHealthOperationsResponse>(
      { url: `/calendar/health/operations`, method: 'POST', params },
      options
    );
  };
  /**
   * Register a calendar webhook subscription for the authenticated user.
   * @summary Register Calendar Subscription
   */
  const calendarRegisterCalendarSubscription = (
    options?: SecondParameter<
      typeof axiosInstance<CalendarSubscriptionRegisteredResponse>
    >
  ) => {
    return axiosInstance<CalendarSubscriptionRegisteredResponse>(
      { url: `/integrations/calendar/subscription`, method: 'POST' },
      options
    );
  };
  /**
 * Disconnect calendar integration for an organization.

This endpoint removes the calendar integration for the specified organization
and provider, cleaning up stored OAuth credentials.

Args:
    org_id: Organization ID (from path, validated against auth context)
    provider: Calendar provider name ("google" or "microsoft")

Returns:
    Dictionary with status and message

Raises:
    HTTPException: 400 for invalid provider, 403 for org mismatch, 500 for server errors
 * @summary Disconnect Calendar
 */
  const calendarDisconnectCalendar = (
    orgId: string,
    provider: string,
    options?: SecondParameter<typeof axiosInstance<CalendarDisconnectResponse>>
  ) => {
    return axiosInstance<CalendarDisconnectResponse>(
      {
        url: `/org/${orgId}/integrations/calendar/${provider}/delete`,
        method: 'DELETE',
      },
      options
    );
  };
  /**
   * Set whether the org uses domain-wide or individual calendar connections.
   * @summary Set Calendar Integration Type
   */
  const calendarSetCalendarIntegrationType = (
    orgId: string,
    setCalendarIntegrationTypePayload: SetCalendarIntegrationTypePayload,
    options?: SecondParameter<
      typeof axiosInstance<SetCalendarIntegrationTypeResponse>
    >
  ) => {
    return axiosInstance<SetCalendarIntegrationTypeResponse>(
      {
        url: `/org/${orgId}/integrations/calendar/integration-type`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: setCalendarIntegrationTypePayload,
      },
      options
    );
  };
  /**
   * Initiate OAuth for an individual user's calendar connection.
   * @summary Initiate Calendar Oauth
   */
  const calendarInitiateCalendarOauth = (
    calendarOAuthInitiatePayload: CalendarOAuthInitiatePayload,
    options?: SecondParameter<
      typeof axiosInstance<CalendarOAuthConsentResponse>
    >
  ) => {
    return axiosInstance<CalendarOAuthConsentResponse>(
      {
        url: `/calendar/oauth/initiate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: calendarOAuthInitiatePayload,
      },
      options
    );
  };
  /**
   * Complete OAuth for an individual user's calendar connection.
   * @summary Handle Calendar Oauth Callback
   */
  const calendarHandleCalendarOauthCallback = (
    calendarOAuthCallbackPayload: CalendarOAuthCallbackPayload,
    options?: SecondParameter<
      typeof axiosInstance<UserCalendarIntegrationResponse>
    >
  ) => {
    return axiosInstance<UserCalendarIntegrationResponse>(
      {
        url: `/calendar/oauth/callback`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: calendarOAuthCallbackPayload,
      },
      options
    );
  };
  /**
   * Get the authenticated user's individual calendar connection status.
   * @summary Get User Calendar Integration
   */
  const calendarGetUserCalendarIntegration = (
    options?: SecondParameter<
      typeof axiosInstance<UserCalendarIntegrationResponse>
    >
  ) => {
    return axiosInstance<UserCalendarIntegrationResponse>(
      { url: `/calendar/user/integration`, method: 'GET' },
      options
    );
  };
  /**
   * Disconnect the authenticated user's individual calendar.
   * @summary Disconnect User Calendar
   */
  const calendarDisconnectUserCalendar = (
    params: CalendarDisconnectUserCalendarParams,
    options?: SecondParameter<typeof axiosInstance<CalendarDisconnectResponse>>
  ) => {
    return axiosInstance<CalendarDisconnectResponse>(
      { url: `/calendar/user/disconnect`, method: 'DELETE', params },
      options
    );
  };
  /**
   * Load optional directory information independently of calendar connection status.
   * @summary Get Calendar Domains
   */
  const calendarGetCalendarDomains = (
    options?: SecondParameter<typeof axiosInstance<DomainsResponse>>
  ) => {
    return axiosInstance<DomainsResponse>(
      { url: `/calendar/domains`, method: 'GET' },
      options
    );
  };
  /**
 * Get calendar integration for an organization.
Returns the connected calendar provider (Google or Microsoft).
 * @summary Get Calendar Integration
 */
  const calendarGetCalendarIntegration = (
    orgId: string,
    userId: string,
    options?: SecondParameter<typeof axiosInstance<CalendarIntegrationResponse>>
  ) => {
    return axiosInstance<CalendarIntegrationResponse>(
      {
        url: `/org/${orgId}/user/${userId}/integrations/calendar`,
        method: 'GET',
      },
      options
    );
  };
  /**
   * Validate domain-wide delegation setup for a user.
   * @summary Validate Domain Wide Delegation
   */
  const calendarValidateDomainWideDelegation = (
    orgId: string,
    userId: string,
    options?: SecondParameter<
      typeof axiosInstance<DomainWideDelegationValidationResponse>
    >
  ) => {
    return axiosInstance<DomainWideDelegationValidationResponse>(
      {
        url: `/org/${orgId}/user/${userId}/integrations/calendar/validate-domain-wide-delegation`,
        method: 'GET',
      },
      options
    );
  };
  /**
 * Get all users in an org's domain.

Args:
    org_id: Organization ID (from path, validated against auth context)

Returns:
    List of users from the domain
 * @summary Get Domain Users
 */
  const calendarGetDomainUsers = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<DomainUsersResponse>>
  ) => {
    return axiosInstance<DomainUsersResponse>(
      { url: `/org/${orgId}/get-domain-users`, method: 'GET' },
      options
    );
  };
  /**
   * Get the authenticated user's auto-join meeting copilot setting.
   * @summary Get User Auto Join Copilot
   */
  const calendarGetUserAutoJoinCopilot = (
    options?: SecondParameter<
      typeof axiosInstance<AutoJoinMeetingCopilotResponse>
    >
  ) => {
    return axiosInstance<AutoJoinMeetingCopilotResponse>(
      { url: `/calendar/user/auto-join-copilot`, method: 'GET' },
      options
    );
  };
  /**
   * Update the authenticated user's auto-join meeting copilot setting.
   * @summary Set User Auto Join Copilot
   */
  const calendarSetUserAutoJoinCopilot = (
    setAutoJoinMeetingCopilotRequest: SetAutoJoinMeetingCopilotRequest,
    options?: SecondParameter<
      typeof axiosInstance<AutoJoinMeetingCopilotResponse>
    >
  ) => {
    return axiosInstance<AutoJoinMeetingCopilotResponse>(
      {
        url: `/calendar/user/auto-join-copilot`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: setAutoJoinMeetingCopilotRequest,
      },
      options
    );
  };
  /**
   * Trigger an immediate scan of upcoming calendar meetings for the authenticated user.
   * @summary Trigger Manual Calendar Scan
   */
  const calendarTriggerManualCalendarScan = (
    options?: SecondParameter<
      typeof axiosInstance<AutoJoinMeetingCopilotResponse>
    >
  ) => {
    return axiosInstance<AutoJoinMeetingCopilotResponse>(
      { url: `/calendar/user/scan-meetings`, method: 'POST' },
      options
    );
  };
  /**
   * Add Virtual Calendar integration for an organization.
   * @summary Add Org Virtual Calendar
   */
  const calendarAddOrgVirtualCalendar = (
    options?: SecondParameter<typeof axiosInstance<DomainsResponse>>
  ) => {
    return axiosInstance<DomainsResponse>(
      { url: `/integrations/virtual-calendar`, method: 'POST' },
      options
    );
  };
  /**
   * List virtual calendars for an organization.
   * @summary List Virtual Calendars
   */
  const calendarListVirtualCalendars = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<VirtualCalendarResponse[]>>
  ) => {
    return axiosInstance<VirtualCalendarResponse[]>(
      { url: `/org/${orgId}/virtual-calendars`, method: 'GET' },
      options
    );
  };
  /**
   * Create a virtual calendar for an organization.
   * @summary Create Virtual Calendar
   */
  const calendarCreateVirtualCalendar = (
    orgId: string,
    createVirtualCalendarPayload: CreateVirtualCalendarPayload,
    options?: SecondParameter<typeof axiosInstance<VirtualCalendarResponse>>
  ) => {
    return axiosInstance<VirtualCalendarResponse>(
      {
        url: `/org/${orgId}/virtual-calendars`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createVirtualCalendarPayload,
      },
      options
    );
  };
  /**
 * Update availability_ranges for multiple virtual calendars at once.
Returns per-calendar success/failure; one failure does not roll back others.
 * @summary Bulk Update Virtual Calendar Availability
 */
  const calendarBulkUpdateVirtualCalendarAvailability = (
    orgId: string,
    bulkUpdateAvailabilityPayload: BulkUpdateAvailabilityPayload,
    options?: SecondParameter<typeof axiosInstance<BulkCalendarUpdateResult[]>>
  ) => {
    return axiosInstance<BulkCalendarUpdateResult[]>(
      {
        url: `/org/${orgId}/virtual-calendars/bulk-availability`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: bulkUpdateAvailabilityPayload,
      },
      options
    );
  };
  /**
 * Update a virtual calendar's description and/or availability_ranges.
Title and email are not editable.
 * @summary Update Virtual Calendar
 */
  const calendarUpdateVirtualCalendar = (
    orgId: string,
    virtualCalendarId: string,
    updateVirtualCalendarPayload: UpdateVirtualCalendarPayload,
    options?: SecondParameter<typeof axiosInstance<VirtualCalendarResponse>>
  ) => {
    return axiosInstance<VirtualCalendarResponse>(
      {
        url: `/org/${orgId}/virtual-calendars/${virtualCalendarId}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: updateVirtualCalendarPayload,
      },
      options
    );
  };
  /**
   * Upsert calendar availability for a user.
   * @summary Upsert Calendar Availability
   */
  const calendarUpsertCalendarAvailability = (
    createCalendarAvailabilityPayload: CreateCalendarAvailabilityPayload,
    options?: SecondParameter<
      typeof axiosInstance<GetCalendarAvailabilityResponse>
    >
  ) => {
    return axiosInstance<GetCalendarAvailabilityResponse>(
      {
        url: `/upsert-calendar-availability`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createCalendarAvailabilityPayload,
      },
      options
    );
  };
  /**
   * Get calendar availability for a user.
   * @summary Get Calendar Availability
   */
  const calendarGetCalendarAvailability = (
    userId: string,
    options?: SecondParameter<
      typeof axiosInstance<GetCalendarAvailabilityResponse>
    >
  ) => {
    return axiosInstance<GetCalendarAvailabilityResponse>(
      { url: `/users/${userId}/calendar-availability`, method: 'GET' },
      options
    );
  };
  /**
   * Perform reschedule of a scheduled interview by sending an SMS to the candidate with alternative times from the recruiter's availability.
   * @summary Scheduled Interview Reschedule
   */
  const calendarScheduledInterviewReschedule = (
    campaignId: string,
    candidateId: string,
    options?: SecondParameter<
      typeof axiosInstance<ScheduledInterviewActionResponse>
    >
  ) => {
    return axiosInstance<ScheduledInterviewActionResponse>(
      {
        url: `/campaigns/${campaignId}/candidates/${candidateId}/scheduled-interview/reschedule`,
        method: 'POST',
      },
      options
    );
  };
  /**
 * Perform cancellation of a scheduled interview: send SMS to the candidate, delete the
meeting from the database, then delete the calendar event. DB is deleted before the
calendar event so that when the provider sends a webhook for the deletion, the webhook
finds no meeting and does not send a duplicate cancel SMS.
 * @summary Scheduled Interview Cancel
 */
  const calendarScheduledInterviewCancel = (
    campaignId: string,
    candidateId: string,
    options?: SecondParameter<
      typeof axiosInstance<ScheduledInterviewActionResponse>
    >
  ) => {
    return axiosInstance<ScheduledInterviewActionResponse>(
      {
        url: `/campaigns/${campaignId}/candidates/${candidateId}/scheduled-interview/cancel`,
        method: 'POST',
      },
      options
    );
  };
  /**
 * Test endpoint for booking a meeting
Payload:
    - candidate_id: Candidate ID
    - organizer_email: Organizer email
    - campaign_id: Campaign ID
    - meeting_provider: Meeting provider to use (ZOOM, TEAMS, GOOGLE_MEET, ONSITE, or NONE)
    - start_time: Start time in ISO format (e.g., 2024-01-15T14:00:00Z)
    - duration_minutes: Meeting duration in minutes
    - onsite_location: Location for onsite meetings (only used with ONSITE provider)

Returns:
    Dictionary containing:
    - meeting_id: The created calendar event ID
    - meeting_url: The meeting join URL (for Zoom/Teams/Meet)
    - status: Booking status
    - start_time: Meeting start time
    - organizer_email: Organizer email
    - candidate details

Raises:
    HTTPException: If org, candidate, or calendar credentials not found
 * @summary Test Book Meeting
 */
  const calendarTestBookMeeting = (
    testBookMeetingPayload: TestBookMeetingPayload,
    options?: SecondParameter<typeof axiosInstance<TestBookMeetingResponse>>
  ) => {
    return axiosInstance<TestBookMeetingResponse>(
      {
        url: `/dev/test_book_meeting`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: testBookMeetingPayload,
      },
      options
    );
  };
  /**
   * Get Zoom credentials for an organization.
   * @summary Get Zoom Credentials
   */
  const calendarGetZoomCredentials = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<ZoomCredentialsResponse>>
  ) => {
    return axiosInstance<ZoomCredentialsResponse>(
      { url: `/orgs/${orgId}/zoom-credentials`, method: 'GET' },
      options
    );
  };
  /**
   * Create or update Zoom credentials for an organization.
   * @summary Save Zoom Credentials
   */
  const calendarSaveZoomCredentials = (
    orgId: string,
    zoomCredentialsPayload: ZoomCredentialsPayload,
    options?: SecondParameter<typeof axiosInstance<ZoomSaveResponse>>
  ) => {
    return axiosInstance<ZoomSaveResponse>(
      {
        url: `/orgs/${orgId}/zoom-credentials`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: zoomCredentialsPayload,
      },
      options
    );
  };
  /**
   * Delete Zoom credentials for an organization.
   * @summary Delete Zoom Credentials
   */
  const calendarDeleteZoomCredentials = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<ZoomSaveResponse>>
  ) => {
    return axiosInstance<ZoomSaveResponse>(
      { url: `/orgs/${orgId}/zoom-credentials`, method: 'DELETE' },
      options
    );
  };
  /**
   * Validate Zoom credentials by attempting to get an access token.
   * @summary Validate Zoom Credentials
   */
  const calendarValidateZoomCredentials = (
    orgId: string,
    zoomCredentialsPayload: ZoomCredentialsPayload,
    options?: SecondParameter<typeof axiosInstance<ZoomValidateResponse>>
  ) => {
    return axiosInstance<ZoomValidateResponse>(
      {
        url: `/orgs/${orgId}/zoom-credentials/validate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: zoomCredentialsPayload,
      },
      options
    );
  };
  return {
    calendarAddOrgGoogleCalendar,
    calendarAddOrgMicrosoftCalendar,
    calendarRunCalendarHealthOperations,
    calendarRegisterCalendarSubscription,
    calendarDisconnectCalendar,
    calendarSetCalendarIntegrationType,
    calendarInitiateCalendarOauth,
    calendarHandleCalendarOauthCallback,
    calendarGetUserCalendarIntegration,
    calendarDisconnectUserCalendar,
    calendarGetCalendarDomains,
    calendarGetCalendarIntegration,
    calendarValidateDomainWideDelegation,
    calendarGetDomainUsers,
    calendarGetUserAutoJoinCopilot,
    calendarSetUserAutoJoinCopilot,
    calendarTriggerManualCalendarScan,
    calendarAddOrgVirtualCalendar,
    calendarListVirtualCalendars,
    calendarCreateVirtualCalendar,
    calendarBulkUpdateVirtualCalendarAvailability,
    calendarUpdateVirtualCalendar,
    calendarUpsertCalendarAvailability,
    calendarGetCalendarAvailability,
    calendarScheduledInterviewReschedule,
    calendarScheduledInterviewCancel,
    calendarTestBookMeeting,
    calendarGetZoomCredentials,
    calendarSaveZoomCredentials,
    calendarDeleteZoomCredentials,
    calendarValidateZoomCredentials,
  };
};
export type CalendarAddOrgGoogleCalendarResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarAddOrgGoogleCalendar']>
  >
>;
export type CalendarAddOrgMicrosoftCalendarResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarAddOrgMicrosoftCalendar']
    >
  >
>;
export type CalendarRunCalendarHealthOperationsResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarRunCalendarHealthOperations']
    >
  >
>;
export type CalendarRegisterCalendarSubscriptionResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarRegisterCalendarSubscription']
    >
  >
>;
export type CalendarDisconnectCalendarResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarDisconnectCalendar']>
  >
>;
export type CalendarSetCalendarIntegrationTypeResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarSetCalendarIntegrationType']
    >
  >
>;
export type CalendarInitiateCalendarOauthResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarInitiateCalendarOauth']>
  >
>;
export type CalendarHandleCalendarOauthCallbackResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarHandleCalendarOauthCallback']
    >
  >
>;
export type CalendarGetUserCalendarIntegrationResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarGetUserCalendarIntegration']
    >
  >
>;
export type CalendarDisconnectUserCalendarResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarDisconnectUserCalendar']>
  >
>;
export type CalendarGetCalendarDomainsResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarGetCalendarDomains']>
  >
>;
export type CalendarGetCalendarIntegrationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarGetCalendarIntegration']>
  >
>;
export type CalendarValidateDomainWideDelegationResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarValidateDomainWideDelegation']
    >
  >
>;
export type CalendarGetDomainUsersResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof getCalendar>['calendarGetDomainUsers']>>
>;
export type CalendarGetUserAutoJoinCopilotResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarGetUserAutoJoinCopilot']>
  >
>;
export type CalendarSetUserAutoJoinCopilotResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarSetUserAutoJoinCopilot']>
  >
>;
export type CalendarTriggerManualCalendarScanResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarTriggerManualCalendarScan']
    >
  >
>;
export type CalendarAddOrgVirtualCalendarResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarAddOrgVirtualCalendar']>
  >
>;
export type CalendarListVirtualCalendarsResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarListVirtualCalendars']>
  >
>;
export type CalendarCreateVirtualCalendarResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarCreateVirtualCalendar']>
  >
>;
export type CalendarBulkUpdateVirtualCalendarAvailabilityResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<
        typeof getCalendar
      >['calendarBulkUpdateVirtualCalendarAvailability']
    >
  >
>;
export type CalendarUpdateVirtualCalendarResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarUpdateVirtualCalendar']>
  >
>;
export type CalendarUpsertCalendarAvailabilityResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarUpsertCalendarAvailability']
    >
  >
>;
export type CalendarGetCalendarAvailabilityResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarGetCalendarAvailability']
    >
  >
>;
export type CalendarScheduledInterviewRescheduleResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarScheduledInterviewReschedule']
    >
  >
>;
export type CalendarScheduledInterviewCancelResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarScheduledInterviewCancel']
    >
  >
>;
export type CalendarTestBookMeetingResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof getCalendar>['calendarTestBookMeeting']>>
>;
export type CalendarGetZoomCredentialsResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarGetZoomCredentials']>
  >
>;
export type CalendarSaveZoomCredentialsResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarSaveZoomCredentials']>
  >
>;
export type CalendarDeleteZoomCredentialsResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getCalendar>['calendarDeleteZoomCredentials']>
  >
>;
export type CalendarValidateZoomCredentialsResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getCalendar>['calendarValidateZoomCredentials']
    >
  >
>;
